import React, { useState, useRef, useEffect } from "react";
import "./PolicyAdjustmentCard.css";

import PolicyStudioActionButton from "./PolicyStudioActionButton";
import { PolicyAdjustment, PolicyAdjustmentType } from "winslow-core";
import ModalConfirm from "./ModalConfirm";

export interface PolicyAdjustmentCardProps {
  adjustment: PolicyAdjustment;
  onUpdate: (newAdjustment: PolicyAdjustment) => void;
  disabled: boolean;
}

export const PolicyAdjustmentCard: React.FC<PolicyAdjustmentCardProps> = ({
  adjustment,
  onUpdate,
  disabled,
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [previewValue, setPreviewValue] = useState(adjustment?.value || "");
  const [editValue, setEditValue] = useState(adjustment?.value || "");
  const [showRevertConfirm, setShowRevertConfirm] = useState(false);
  const [lastSubmittedValue, setLastSubmittedValue] = useState(
    adjustment?.value || ""
  );
  const isDirty = editValue !== previewValue;

  useEffect(() => {
    setEditValue(adjustment?.value || "");
    setPreviewValue(adjustment?.value || "");
    setLastSubmittedValue(adjustment?.value || "");
  }, [adjustment]);

  const handleSubmit = (shouldRevert = false) => {
    if (disabled) return;

    const newAdjustment = {
      ...adjustment,
      value: shouldRevert ? lastSubmittedValue : editValue,
      policyAdjustmentType: shouldRevert
        ? PolicyAdjustmentType.REVERT_ADJUSTMENT
        : PolicyAdjustmentType.UPDATE_ADJUSTMENT,
    };

    onUpdate(newAdjustment);
    setIsOpen(false);
    if (!shouldRevert) {
      setLastSubmittedValue(editValue);
    }
    setPreviewValue(shouldRevert ? lastSubmittedValue : editValue);
    setShowRevertConfirm(false);
  };

  const handleUpdateClick = () => {
    if (editValue.trim() === "") {
      setShowRevertConfirm(true);
    } else {
      handleSubmit(false);
    }
  };

  const handleRevertClick = () => {
    setShowRevertConfirm(true);
  };

  const handleToggleOpen = (e?: React.MouseEvent) => {
    if (disabled) return;
    e?.stopPropagation();
    setIsOpen(!isOpen);
  };

  return (
    <div
      className={`policy-adjustment ${isOpen ? "open" : ""} ${disabled ? "disabled" : ""}`}
      onClick={() => !isOpen && handleToggleOpen()}>
      <div
        className="policy-adjustment-header"
        onClick={(e) => {
          e.stopPropagation();
          handleToggleOpen(e);
        }}>
        <span className={`policy-adjustment-title ${isOpen ? "open" : ""}`}>
          {adjustment.name}
        </span>

        <img
          src="./icons/light-mode/down.svg"
          alt=""
          className={`policy-adjustment-icon ${isOpen ? "rotated" : ""}`}
          loading="lazy"
        />
      </div>

      {!isOpen && previewValue && (
        <div className="policy-adjustment-preview">{previewValue}</div>
      )}

      {isOpen && (
        <div className="policy-adjustment-description">
          <textarea
            ref={textareaRef}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            aria-label={`${adjustment.name} policy configuration`}
            placeholder="Enter policy configuration..."
            rows={4}
            disabled={disabled}
          />
        </div>
      )}

      {isDirty && isOpen && (
        <div className="policy-adjustment-actions">
          <img
            src="./icons/policy-studio-trash.svg"
            alt=""
            className="policy-adjustment-update-icon"
            loading="lazy"
            onClick={handleRevertClick}
          />
          <PolicyStudioActionButton
            label="Update"
            variant="primary"
            onClick={handleUpdateClick}
            disabled={disabled}
          />
        </div>
      )}
      <ModalConfirm
        text="This will revert the policy adjustment.  Are you sure you want to continue?"
        isOpen={showRevertConfirm}
        onClose={() => setShowRevertConfirm(false)}
        onContinue={() => handleSubmit(true)}
      />
    </div>
  );
};

export default PolicyAdjustmentCard;
